/**
 * Qwen3-ASR-Flash-Realtime 客户端
 *
 * 浏览器麦克风 → 16kHz mono PCM16 → base64 → /api/asr (WebSocket) → 文本回调
 *
 * 用法：
 *   const asr = new QwenAsr({ onText, onError });
 *   await asr.start();
 *   ...
 *   asr.stop();
 */

interface Options {
	onInterim?: (text: string) => void;
	onFinal?: (text: string) => void;
	onError?: (msg: string) => void;
	onClose?: () => void;
}

const TARGET_SAMPLE_RATE = 16000;
const CHUNK_SIZE = 4096; // 浏览器音频帧大小（约 100ms 在 16kHz 下）

export class QwenAsr {
	private ws: WebSocket | null = null;
	private audioCtx: AudioContext | null = null;
	private stream: MediaStream | null = null;
	private processor: ScriptProcessorNode | null = null;
	private source: MediaStreamAudioSourceNode | null = null;
	private opts: Options;
	private active = false;

	constructor(opts: Options) {
		this.opts = opts;
	}

	async start(): Promise<void> {
		if (this.active) return;
		this.active = true;

		// 1. 拿麦克风
		try {
			this.stream = await navigator.mediaDevices.getUserMedia({
				audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
			});
		} catch (err) {
			this.opts.onError?.('麦克风权限被拒绝：' + (err as Error).message);
			this.cleanup();
			return;
		}

		// 2. 打开 WebSocket
		const wsUrl = `${location.origin.replace(/^http/, 'ws')}/api/asr`;
		this.ws = new WebSocket(wsUrl);
		await new Promise<void>((resolve, reject) => {
			if (!this.ws) return reject(new Error('no ws'));
			this.ws.onopen = () => resolve();
			this.ws.onerror = (e) => reject(new Error('ws error: ' + (e as any).message));
			setTimeout(() => reject(new Error('ws connect timeout')), 8000);
		}).catch((err) => {
			this.opts.onError?.(err.message);
			this.cleanup();
			throw err;
		});
		if (!this.active || !this.ws) return; // 用户已取消

		// 3. 监听服务端事件
		this.ws.onmessage = (e) => {
			let evt: any;
			try { evt = JSON.parse(typeof e.data === 'string' ? e.data : ''); } catch { return; }
			const t = evt.type as string;
			if (t === 'conversation.item.input_audio_transcription.delta' || t === 'conversation.item.input_audio_transcription.text') {
				const txt = evt.delta ?? evt.transcript ?? '';
				if (txt) this.opts.onInterim?.(txt);
			} else if (t === 'conversation.item.input_audio_transcription.completed') {
				const txt = evt.transcript ?? '';
				if (txt) this.opts.onFinal?.(txt);
			} else if (t === 'error') {
				this.opts.onError?.(evt.error?.message || evt.message || 'ASR 出错');
			}
		};
		this.ws.onclose = () => {
			this.opts.onClose?.();
			this.cleanup();
		};

		// 4. 发 session.update 配置
		this.ws.send(
			JSON.stringify({
				type: 'session.update',
				session: {
					modalities: ['text'],
					input_audio_format: 'pcm16',
					input_audio_sample_rate: TARGET_SAMPLE_RATE,
					input_audio_transcription: { model: 'qwen3-asr-flash-realtime', language: 'zh' },
					turn_detection: { type: 'server_vad', threshold: 0.5, silence_duration_ms: 600 },
				},
			})
		);

		// 5. 启动音频管道
		this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
		const sourceRate = this.audioCtx.sampleRate;
		this.source = this.audioCtx.createMediaStreamSource(this.stream);
		this.processor = this.audioCtx.createScriptProcessor(CHUNK_SIZE, 1, 1);
		this.source.connect(this.processor);
		this.processor.connect(this.audioCtx.destination);

		this.processor.onaudioprocess = (e) => {
			if (!this.active || !this.ws || this.ws.readyState !== 1) return;
			const float = e.inputBuffer.getChannelData(0);
			const downsampled = downsample(float, sourceRate, TARGET_SAMPLE_RATE);
			const pcm16 = floatToPCM16(downsampled);
			const b64 = bytesToBase64(new Uint8Array(pcm16.buffer));
			try {
				this.ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: b64 }));
			} catch {}
		};
	}

	stop(): void {
		if (!this.active) return;
		try {
			if (this.ws && this.ws.readyState === 1) {
				this.ws.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
				this.ws.send(JSON.stringify({ type: 'session.finish' }));
			}
		} catch {}
		setTimeout(() => this.cleanup(), 800);
	}

	private cleanup() {
		this.active = false;
		try { this.processor?.disconnect(); } catch {}
		try { this.source?.disconnect(); } catch {}
		try { this.audioCtx?.close(); } catch {}
		this.stream?.getTracks().forEach((t) => t.stop());
		this.processor = null;
		this.source = null;
		this.audioCtx = null;
		this.stream = null;
		try { this.ws?.close(); } catch {}
		this.ws = null;
	}
}

// ── 工具函数 ──
function downsample(buf: Float32Array, fromRate: number, toRate: number): Float32Array {
	if (fromRate === toRate) return buf;
	const ratio = fromRate / toRate;
	const newLen = Math.round(buf.length / ratio);
	const out = new Float32Array(newLen);
	for (let i = 0; i < newLen; i++) {
		const start = Math.floor(i * ratio);
		const end = Math.floor((i + 1) * ratio);
		let sum = 0;
		let count = 0;
		for (let j = start; j < end && j < buf.length; j++) {
			sum += buf[j];
			count++;
		}
		out[i] = count > 0 ? sum / count : 0;
	}
	return out;
}

function floatToPCM16(buf: Float32Array): Int16Array {
	const out = new Int16Array(buf.length);
	for (let i = 0; i < buf.length; i++) {
		const s = Math.max(-1, Math.min(1, buf[i]));
		out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
	}
	return out;
}

function bytesToBase64(bytes: Uint8Array): string {
	let bin = '';
	const CHUNK = 0x8000;
	for (let i = 0; i < bytes.length; i += CHUNK) {
		bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
	}
	return btoa(bin);
}
